import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Plus, Edit, Trash2, GraduationCap } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const Competencias180 = () => {
  const [competencias, setCompetencias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ nome: '', descricao: '', categoria: '', ordem: 0 });

  useEffect(() => {
    fetchCompetencias();
  }, []);

  const fetchCompetencias = async () => {
    try {
      const response = await axios.get(`${API}/api/feedback-180/competencias`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setCompetencias(response.data);
    } catch (error) {
      toast.error('Erro ao carregar competências');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      await axios.post(`${API}/api/feedback-180/competencias`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success('Competência criada!');
      fetchCompetencias();
      handleCloseDialog();
    } catch (error) {
      toast.error('Erro ao criar competência');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingId(null);
    setFormData({ nome: '', descricao: '', categoria: '', ordem: 0 });
  };

  const criarCompetenciasPadrao = async () => {
    const padrao = [
      { nome: 'Comunicação', descricao: 'Capacidade de se expressar claramente e ouvir ativamente', categoria: 'Comportamental', ordem: 1 },
      { nome: 'Trabalho em Equipe', descricao: 'Colabora efetivamente com colegas para atingir objetivos comuns', categoria: 'Comportamental', ordem: 2 },
      { nome: 'Liderança', descricao: 'Inspira e motiva outros, toma decisões assertivas', categoria: 'Liderança', ordem: 3 },
      { nome: 'Resolução de Problemas', descricao: 'Identifica soluções criativas para desafios complexos', categoria: 'Técnica', ordem: 4 },
      { nome: 'Proatividade', descricao: 'Toma iniciativa e age antes de ser solicitado', categoria: 'Comportamental', ordem: 5 },
    ];

    try {
      setLoading(true);
      for (const comp of padrao) {
        await axios.post(`${API}/api/feedback-180/competencias`, comp, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
      }
      toast.success('Competências padrão criadas!');
      fetchCompetencias();
    } catch (error) {
      toast.error('Erro ao criar competências padrão');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Competências - Feedback 180º</h1>
          <p className="text-gray-600 mt-1">Gerencie as competências avaliadas no feedback</p>
        </div>
        
        <div className="flex space-x-2">
          {competencias.length === 0 && (
            <Button onClick={criarCompetenciasPadrao} variant="outline" disabled={loading}>
              <Plus className="h-4 w-4 mr-2" />
              Criar Padrões
            </Button>
          )}
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-purple-600 hover:bg-purple-700">
                <Plus className="h-4 w-4 mr-2" />
                Nova Competência
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nova Competência</DialogTitle>
                <DialogDescription>
                  Defina uma nova competência para avaliar no feedback 180º
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nome *</Label>
                  <Input
                    value={formData.nome}
                    onChange={(e) => setFormData({...formData, nome: e.target.value})}
                    placeholder="Ex: Comunicação"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Descrição Comportamental *</Label>
                  <Textarea
                    value={formData.descricao}
                    onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                    placeholder="Descreva o comportamento esperado..."
                    rows={3}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Categoria</Label>
                  <Input
                    value={formData.categoria}
                    onChange={(e) => setFormData({...formData, categoria: e.target.value})}
                    placeholder="Ex: Comportamental, Técnica, Liderança"
                  />
                </div>
                
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={handleCloseDialog}>
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={loading} className="bg-purple-600">
                    {loading ? 'Criando...' : 'Criar'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {competencias.map((comp) => (
          <Card key={comp.id}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg">{comp.nome}</CardTitle>
                  {comp.categoria && (
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded mt-1 inline-block">
                      {comp.categoria}
                    </span>
                  )}
                </div>
                <GraduationCap className="h-5 w-5 text-purple-600" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">{comp.descricao}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {competencias.length === 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <GraduationCap className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">Nenhuma competência cadastrada</p>
              <Button onClick={criarCompetenciasPadrao} disabled={loading}>
                <Plus className="h-4 w-4 mr-2" />
                Criar Competências Padrão
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Competencias180;
